import type {
  ComponentSize,
  ComponentVariant,
  ComponentAppearance,
} from 'src/modules/shared/types/component.type';
import { controlTriggerSizeClasses } from 'src/modules/shared/styles/component.styles';

export const tabBaseClasses: string[] = [
  'inline-flex',
  'items-center',
  'font-medium',
  'rounded-md',
  'transition-all',
  'duration-200',
  'cursor-pointer',
  'outline-none',
  'focus:outline-none',
  'focus:!ring-0',
  'focus:!ring-offset-0',
  'active:!ring-0',
  'active:!ring-offset-0',
  'disabled:opacity-50',
  'disabled:cursor-not-allowed',
  'shrink-0', // Prevent tabs from shrinking too much when wrapping
];

const tabPaddingClasses: Record<ComponentSize, string> = {
  '2xs': 'px-2',
  xs: 'px-2.5',
  sm: 'px-3',
  md: 'px-4',
  lg: 'px-5',
};

export const tabSizeClasses: Record<ComponentSize, string> = {
  '2xs': `${controlTriggerSizeClasses['2xs']} ${tabPaddingClasses['2xs']}`,
  xs: `${controlTriggerSizeClasses.xs} ${tabPaddingClasses.xs}`,
  sm: `${controlTriggerSizeClasses.sm} ${tabPaddingClasses.sm}`,
  md: `${controlTriggerSizeClasses.md} ${tabPaddingClasses.md}`,
  lg: `${controlTriggerSizeClasses.lg} ${tabPaddingClasses.lg}`,
};

export const tabAppearanceClasses: Record<
  NonNullable<ComponentAppearance>,
  Record<ComponentVariant, string>
> = {
  filled: {
    primary:
      'bg-transparent text-primary-700 hover:bg-primary-100 active:bg-primary-200 disabled:bg-transparent disabled:hover:bg-transparent disabled:text-primary-700',
    secondary:
      'bg-transparent text-secondary-700 hover:bg-secondary-100 active:bg-secondary-200 disabled:bg-transparent disabled:hover:bg-transparent disabled:text-secondary-700',
    danger:
      'bg-transparent text-danger-700 hover:bg-danger-100 active:bg-danger-200 disabled:bg-transparent disabled:hover:bg-transparent disabled:text-danger-700',
    warning:
      'bg-transparent text-warning-700 hover:bg-warning-100 active:bg-warning-200 disabled:bg-transparent disabled:hover:bg-transparent disabled:text-warning-700',
    neutral:
      'bg-transparent text-neutral-700 hover:bg-neutral-100 active:bg-neutral-200 disabled:bg-transparent disabled:hover:bg-transparent disabled:text-neutral-700',
    success:
      'bg-transparent text-success-700 hover:bg-success-100 active:bg-success-200 disabled:bg-transparent disabled:hover:bg-transparent disabled:text-success-700',
    info: 'bg-transparent text-info-700 hover:bg-info-100 active:bg-info-200 disabled:bg-transparent disabled:hover:bg-transparent disabled:text-info-700',
    whatsapp:
      'bg-transparent text-whatsapp-700 hover:bg-whatsapp-100 active:bg-whatsapp-200 disabled:bg-transparent disabled:hover:bg-transparent disabled:text-whatsapp-700',
    call: 'bg-transparent text-call-700 hover:bg-call-100 active:bg-call-200 disabled:bg-transparent disabled:hover:bg-transparent disabled:text-call-700',
    email:
      'bg-transparent text-email-700 hover:bg-email-100 active:bg-email-200 disabled:bg-transparent disabled:hover:bg-transparent disabled:text-email-700',
  },
  outline: {
    primary:
      'bg-transparent text-primary-600 hover:bg-primary-50 active:bg-primary-100 disabled:bg-transparent disabled:hover:bg-transparent disabled:text-primary-600',
    secondary:
      'bg-transparent text-secondary-600 hover:bg-secondary-50 active:bg-secondary-100 disabled:bg-transparent disabled:hover:bg-transparent disabled:text-secondary-600',
    danger:
      'bg-transparent text-danger-600 hover:bg-danger-50 active:bg-danger-100 disabled:bg-transparent disabled:hover:bg-transparent disabled:text-danger-600',
    warning:
      'bg-transparent text-warning-600 hover:bg-warning-50 active:bg-warning-100 disabled:bg-transparent disabled:hover:bg-transparent disabled:text-warning-600',
    neutral:
      'bg-transparent text-neutral-600 hover:bg-neutral-50 active:bg-neutral-100 disabled:bg-transparent disabled:hover:bg-transparent disabled:text-neutral-600',
    success:
      'bg-transparent text-success-600 hover:bg-success-50 active:bg-success-100 disabled:bg-transparent disabled:hover:bg-transparent disabled:text-success-600',
    info: 'bg-transparent text-info-600 hover:bg-info-50 active:bg-info-100 disabled:bg-transparent disabled:hover:bg-transparent disabled:text-info-600',
    whatsapp:
      'bg-transparent text-whatsapp-600 hover:bg-whatsapp-50 active:bg-whatsapp-100 disabled:bg-transparent disabled:hover:bg-transparent disabled:text-whatsapp-600',
    call: 'bg-transparent text-call-600 hover:bg-call-50 active:bg-call-100 disabled:bg-transparent disabled:hover:bg-transparent disabled:text-call-600',
    email:
      'bg-transparent text-email-600 hover:bg-email-50 active:bg-email-100 disabled:bg-transparent disabled:hover:bg-transparent disabled:text-email-600',
  },
  soft: {
    primary:
      'bg-transparent text-primary-600 hover:bg-primary-100 active:bg-primary-200 disabled:bg-transparent disabled:hover:bg-transparent disabled:text-primary-600',
    secondary:
      'bg-transparent text-secondary-600 hover:bg-secondary-100 active:bg-secondary-200 disabled:bg-transparent disabled:hover:bg-transparent disabled:text-secondary-600',
    danger:
      'bg-transparent text-danger-600 hover:bg-danger-100 active:bg-danger-200 disabled:bg-transparent disabled:hover:bg-transparent disabled:text-danger-600',
    warning:
      'bg-transparent text-warning-600 hover:bg-warning-100 active:bg-warning-200 disabled:bg-transparent disabled:hover:bg-transparent disabled:text-warning-600',
    neutral:
      'bg-transparent text-neutral-600 hover:bg-neutral-100 active:bg-neutral-200 disabled:bg-transparent disabled:hover:bg-transparent disabled:text-neutral-600',
    success:
      'bg-transparent text-success-600 hover:bg-success-100 active:bg-success-200 disabled:bg-transparent disabled:hover:bg-transparent disabled:text-success-600',
    info: 'bg-transparent text-info-600 hover:bg-info-100 active:bg-info-200 disabled:bg-transparent disabled:hover:bg-transparent disabled:text-info-600',
    whatsapp:
      'bg-transparent text-whatsapp-600 hover:bg-whatsapp-100 active:bg-whatsapp-200 disabled:bg-transparent disabled:hover:bg-transparent disabled:text-whatsapp-600',
    call: 'bg-transparent text-call-600 hover:bg-call-100 active:bg-call-200 disabled:bg-transparent disabled:hover:bg-transparent disabled:text-call-600',
    email:
      'bg-transparent text-email-600 hover:bg-email-100 active:bg-email-200 disabled:bg-transparent disabled:hover:bg-transparent disabled:text-email-600',
  },
  ghost: {
    primary:
      'bg-transparent text-primary-600 hover:bg-primary-50 active:bg-primary-100 disabled:bg-transparent disabled:hover:bg-transparent disabled:text-primary-600',
    secondary:
      'bg-transparent text-secondary-600 hover:bg-secondary-50 active:bg-secondary-100 disabled:bg-transparent disabled:hover:bg-transparent disabled:text-secondary-600',
    danger:
      'bg-transparent text-danger-600 hover:bg-danger-50 active:bg-danger-100 disabled:bg-transparent disabled:hover:bg-transparent disabled:text-danger-600',
    warning:
      'bg-transparent text-warning-600 hover:bg-warning-50 active:bg-warning-100 disabled:bg-transparent disabled:hover:bg-transparent disabled:text-warning-600',
    neutral:
      'bg-transparent text-neutral-600 hover:bg-neutral-50 active:bg-neutral-100 disabled:bg-transparent disabled:hover:bg-transparent disabled:text-neutral-600',
    success:
      'bg-transparent text-success-600 hover:bg-success-50 active:bg-success-100 disabled:bg-transparent disabled:hover:bg-transparent disabled:text-success-600',
    info: 'bg-transparent text-info-600 hover:bg-info-50 active:bg-info-100 disabled:bg-transparent disabled:hover:bg-transparent disabled:text-info-600',
    whatsapp:
      'bg-transparent text-whatsapp-600 hover:bg-whatsapp-50 active:bg-whatsapp-100 disabled:bg-transparent disabled:hover:bg-transparent disabled:text-whatsapp-600',
    call: 'bg-transparent text-call-600 hover:bg-call-50 active:bg-call-100 disabled:bg-transparent disabled:hover:bg-transparent disabled:text-call-600',
    email:
      'bg-transparent text-email-600 hover:bg-email-50 active:bg-email-100 disabled:bg-transparent disabled:hover:bg-transparent disabled:text-email-600',
  },
};

export const tabActiveClasses: Record<
  NonNullable<ComponentAppearance>,
  Record<ComponentVariant, string>
> = {
  filled: {
    primary:
      'bg-primary-500 !text-white shadow-sm hover:bg-primary-500 active:bg-primary-500 disabled:bg-primary-500 disabled:hover:bg-primary-500',
    secondary:
      'bg-secondary-500 !text-white shadow-sm hover:bg-secondary-500 active:bg-secondary-500 disabled:bg-secondary-500 disabled:hover:bg-secondary-500',
    danger:
      'bg-danger-500 !text-white shadow-sm hover:bg-danger-500 active:bg-danger-500 disabled:bg-danger-500 disabled:hover:bg-danger-500',
    warning:
      'bg-warning-500 !text-white shadow-sm hover:bg-warning-500 active:bg-warning-500 disabled:bg-warning-500 disabled:hover:bg-warning-500',
    neutral:
      'bg-neutral-200 text-neutral-900 shadow-sm hover:bg-neutral-200 active:bg-neutral-200 disabled:bg-neutral-200 disabled:hover:bg-neutral-200',
    success:
      'bg-success-500 !text-white shadow-sm hover:bg-success-500 active:bg-success-500 disabled:bg-success-500 disabled:hover:bg-success-500',
    info: 'bg-info-500 !text-white shadow-sm hover:bg-info-500 active:bg-info-500 disabled:bg-info-500 disabled:hover:bg-info-500',
    whatsapp:
      'bg-whatsapp-500 !text-white shadow-sm hover:bg-whatsapp-500 active:bg-whatsapp-500 disabled:bg-whatsapp-500 disabled:hover:bg-whatsapp-500',
    call: 'bg-call-500 !text-white shadow-sm hover:bg-call-500 active:bg-call-500 disabled:bg-call-500 disabled:hover:bg-call-500',
    email:
      'bg-email-500 !text-white shadow-sm hover:bg-email-500 active:bg-email-500 disabled:bg-email-500 disabled:hover:bg-email-500',
  },
  outline: {
    primary:
      'bg-primary-50 text-primary-700 border border-primary-300 shadow-sm hover:bg-primary-50 active:bg-primary-50 disabled:bg-primary-50 disabled:hover:bg-primary-50 disabled:border-primary-300',
    secondary:
      'bg-secondary-50 text-secondary-700 border border-secondary-300 shadow-sm hover:bg-secondary-50 active:bg-secondary-50 disabled:bg-secondary-50 disabled:hover:bg-secondary-50 disabled:border-secondary-300',
    danger:
      'bg-danger-50 text-danger-700 border border-danger-300 shadow-sm hover:bg-danger-50 active:bg-danger-50 disabled:bg-danger-50 disabled:hover:bg-danger-50 disabled:border-danger-300',
    warning:
      'bg-warning-50 text-warning-700 border border-warning-300 shadow-sm hover:bg-warning-50 active:bg-warning-50 disabled:bg-warning-50 disabled:hover:bg-warning-50 disabled:border-warning-300',
    neutral:
      'bg-neutral-50 text-neutral-700 border border-neutral-300 shadow-sm hover:bg-neutral-50 active:bg-neutral-50 disabled:bg-neutral-50 disabled:hover:bg-neutral-50 disabled:border-neutral-300',
    success:
      'bg-success-50 text-success-700 border border-success-300 shadow-sm hover:bg-success-50 active:bg-success-50 disabled:bg-success-50 disabled:hover:bg-success-50 disabled:border-success-300',
    info: 'bg-info-50 text-info-700 border border-info-300 shadow-sm hover:bg-info-50 active:bg-info-50 disabled:bg-info-50 disabled:hover:bg-info-50 disabled:border-info-300',
    whatsapp:
      'bg-whatsapp-50 text-whatsapp-700 border border-whatsapp-300 shadow-sm hover:bg-whatsapp-50 active:bg-whatsapp-50 disabled:bg-whatsapp-50 disabled:hover:bg-whatsapp-50 disabled:border-whatsapp-300',
    call: 'bg-call-50 text-call-700 border border-call-300 shadow-sm hover:bg-call-50 active:bg-call-50 disabled:bg-call-50 disabled:hover:bg-call-50 disabled:border-call-300',
    email:
      'bg-email-50 text-email-700 border border-email-300 shadow-sm hover:bg-email-50 active:bg-email-50 disabled:bg-email-50 disabled:hover:bg-email-50 disabled:border-email-300',
  },
  soft: {
    primary:
      'bg-primary-200 text-primary-800 shadow-sm hover:bg-primary-200 active:bg-primary-200 disabled:bg-primary-200 disabled:hover:bg-primary-200',
    secondary:
      'bg-secondary-200 text-secondary-800 shadow-sm hover:bg-secondary-200 active:bg-secondary-200 disabled:bg-secondary-200 disabled:hover:bg-secondary-200',
    danger:
      'bg-danger-200 text-danger-800 shadow-sm hover:bg-danger-200 active:bg-danger-200 disabled:bg-danger-200 disabled:hover:bg-danger-200',
    warning:
      'bg-warning-200 text-warning-800 shadow-sm hover:bg-warning-200 active:bg-warning-200 disabled:bg-warning-200 disabled:hover:bg-warning-200',
    neutral:
      'bg-neutral-200 text-neutral-800 shadow-sm hover:bg-neutral-200 active:bg-neutral-200 disabled:bg-neutral-200 disabled:hover:bg-neutral-200',
    success:
      'bg-success-200 text-success-800 shadow-sm hover:bg-success-200 active:bg-success-200 disabled:bg-success-200 disabled:hover:bg-success-200',
    info: 'bg-info-200 text-info-800 shadow-sm hover:bg-info-200 active:bg-info-200 disabled:bg-info-200 disabled:hover:bg-info-200',
    whatsapp:
      'bg-whatsapp-200 text-whatsapp-800 shadow-sm hover:bg-whatsapp-200 active:bg-whatsapp-200 disabled:bg-whatsapp-200 disabled:hover:bg-whatsapp-200',
    call: 'bg-call-200 text-call-800 shadow-sm hover:bg-call-200 active:bg-call-200 disabled:bg-call-200 disabled:hover:bg-call-200',
    email:
      'bg-email-200 text-email-800 shadow-sm hover:bg-email-200 active:bg-email-200 disabled:bg-email-200 disabled:hover:bg-email-200',
  },
  ghost: {
    primary:
      'bg-primary-100 text-primary-700 shadow-sm hover:bg-primary-100 active:bg-primary-100 disabled:bg-primary-100 disabled:hover:bg-primary-100',
    secondary:
      'bg-secondary-100 text-secondary-700 shadow-sm hover:bg-secondary-100 active:bg-secondary-100 disabled:bg-secondary-100 disabled:hover:bg-secondary-100',
    danger:
      'bg-danger-100 text-danger-700 shadow-sm hover:bg-danger-100 active:bg-danger-100 disabled:bg-danger-100 disabled:hover:bg-danger-100',
    warning:
      'bg-warning-100 text-warning-700 shadow-sm hover:bg-warning-100 active:bg-warning-100 disabled:bg-warning-100 disabled:hover:bg-warning-100',
    neutral:
      'bg-neutral-100 text-neutral-700 shadow-sm hover:bg-neutral-100 active:bg-neutral-100 disabled:bg-neutral-100 disabled:hover:bg-neutral-100',
    success:
      'bg-success-100 text-success-700 shadow-sm hover:bg-success-100 active:bg-success-100 disabled:bg-success-100 disabled:hover:bg-success-100',
    info: 'bg-info-100 text-info-700 shadow-sm hover:bg-info-100 active:bg-info-100 disabled:bg-info-100 disabled:hover:bg-info-100',
    whatsapp:
      'bg-whatsapp-100 text-whatsapp-700 shadow-sm hover:bg-whatsapp-100 active:bg-whatsapp-100 disabled:bg-whatsapp-100 disabled:hover:bg-whatsapp-100',
    call: 'bg-call-100 text-call-700 shadow-sm hover:bg-call-100 active:bg-call-100 disabled:bg-call-100 disabled:hover:bg-call-100',
    email:
      'bg-email-100 text-email-700 shadow-sm hover:bg-email-100 active:bg-email-100 disabled:bg-email-100 disabled:hover:bg-email-100',
  },
};
